/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import moment, { Moment } from "moment-timezone"
import { SearchParams } from "../constants/search_parameters"
import {
  dateTimeRebasedUTC,
  isParsedUTCDateValid,
  nextUTCDate,
} from "./datetime"
import {
  getSearchParams,
  updateSearchParams,
} from "./uri"
import { GKMM_DEPLOY_TIMESTAMP_ML } from "../constants/misc"

export const shouldShowGKMMError = (
  shortOrderID: string, 
  orderUpdateTime: Timestamp
  ): boolean => {
    var s = shortOrderID.split("-")
    return s.length > 1 ? s[1].includes("M") && orderUpdateTime < GKMM_DEPLOY_TIMESTAMP_ML : false
}

export const parseDateRangeParamsFromURI = (
  timezone?: string,
): [
  // UTC of merchant local date in RFC3339 format
  string | undefined,
  string | undefined,
] => {
  const searchParams = getSearchParams()
  const startDate = searchParams.get(
    SearchParams.startDate,
  )
  if (
    startDate === null ||
    Number.isNaN(parseInt(startDate, 10))
  ) {
    searchParams.delete(SearchParams.startDate)
    searchParams.delete(SearchParams.endDate)
    return [undefined, undefined]
  }
  const startDateMoment = moment
    .utc(parseInt(startDate, 10))
    .startOf("hour")
  if (
    !isParsedUTCDateValid(
      startDateMoment,
      timezone,
    )
  ) {
    searchParams.delete(SearchParams.startDate)
    searchParams.delete(SearchParams.endDate)
    return [undefined, undefined]
  }
  const endDate = searchParams.get(
    SearchParams.endDate,
  )
  let endDateMoment =
    endDate === null ||
    Number.isNaN(parseInt(endDate, 10))
      ? null
      : moment.utc(parseInt(endDate, 10))
  if (
    endDateMoment === null ||
    !isParsedUTCDateValid(endDateMoment, timezone)
  ) {
    endDateMoment = nextUTCDate(startDateMoment)
  }
  updateSearchParams([
    {
      key: SearchParams.startDate,
      value: startDateMoment.valueOf(),
    },
    {
      key: SearchParams.endDate,
      value: endDateMoment.valueOf(),
    },
  ])
  return [
    startDateMoment.format(),
    endDateMoment.format(),
  ]
}

const resetDateRangeParamsWithStartDate = (
  timezone: string,
  browserLocalStartDate: Moment,
) => {
  const startDateMoment = dateTimeRebasedUTC(
    browserLocalStartDate.valueOf(),
    timezone,
    false,
  ) as Moment
  const endDateMoment = nextUTCDate(
    startDateMoment,
  )
  updateSearchParams([
    {
      key: SearchParams.startDate,
      value: startDateMoment.valueOf(),
    },
    {
      key: SearchParams.endDate,
      value: endDateMoment.valueOf(),
    },
  ])
}

const resetDateRangeParamsWithTimeZone = (
  timezone: string,
) => {
  const searchParams = getSearchParams()
  const startDate = searchParams.get(
    SearchParams.startDate,
  )
  let startDateMoment = null
  if (
    startDate !== null &&
    !Number.isNaN(parseInt(startDate, 10))
  ) {
    startDateMoment = moment.utc(
      parseInt(startDate, 10),
    )
  }
  if (
    startDateMoment === null ||
    !isParsedUTCDateValid(
      startDateMoment,
      timezone,
    )
  ) {
    startDateMoment = moment
      .tz(timezone)
      .startOf("day")
      .utc()
  }
  const endDateMoment = nextUTCDate(
    startDateMoment,
  )
  updateSearchParams([
    {
      key: SearchParams.startDate,
      value: startDateMoment.valueOf(),
    },
    {
      key: SearchParams.endDate,
      value: endDateMoment.valueOf(),
    },
  ])
}

export function resetDateRangeParams(
  timezone: string,
  browserLocalStartDate?: Moment,
  // endLocaleDate?: Moment,
): void {
  if (
    browserLocalStartDate === undefined
    // endLocaleDate === undefined
  ) {
    return resetDateRangeParamsWithTimeZone(
      timezone,
    )
  }
  if (
    browserLocalStartDate
    // endLocaleDate === undefined
  ) {
    return resetDateRangeParamsWithStartDate(
      timezone,
      browserLocalStartDate,
    )
  }
  return undefined
}

export const warningInSwitchDefaultCase = <
  T = never
>(
  _: T,
  returnVal: any = undefined,
) => returnVal
